import { NestedFilter } from '@@partner:types:graphql'

import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import React, { useEffect, useMemo, useState } from 'react'

import { useGridHelper } from '@@partner:helpers/crud/grid'
import { useFetchAllMainPageConfigs } from '@@partner:graphql/query/mainPageConfig/allMainPageConfigs.gql'
import { Button, ButtonGroup, Divider, FlexboxGrid, List, Pagination } from 'rsuite'
import ConfigListItem from './ConfigListItem'
import { usePartner } from '@@partner:helpers/hooks/auth'
import { generatePath, Link } from 'react-router-dom'
import { ROUTE_MAIN_PAGE_CONFIGS_ADD } from '@@partner:config/routes'
import { MAIN_PAGE_CONFIG_TYPE_OPTIONS } from './constants'
import { MainPageConfigType } from '@@partner:types:app'

const defaultMainPageConfigFilter: NestedFilter = {
  fields: [ 'id' ],
  op: 'unwrap'
}

export default function MainPageConfigsGrid () {
  const { brandSlug } = usePartner()
  const { page, tableSort, handlePageSelect, handleSortColumn } = useGridHelper()
  const [ selectedType, setSelectedType ] = useState<MainPageConfigType>('delivery')

  const mainPageConfigFilter = useMemo<NestedFilter>(() => ({
    ...defaultMainPageConfigFilter,
    and: [
      {
        fields: [ 'type' ],
        op: 'eq',
        value: [ selectedType ]
      }
    ]
  }), [ selectedType ])

  const { data, loading, error } = useFetchAllMainPageConfigs({
    filter: mainPageConfigFilter,
    pager: { page },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const configs = data?.allMainPageConfigs?.edges ?? []
  const today = new Date()

  useEffect(() => {
    handlePageSelect(1)
  }, [ selectedType, handlePageSelect ])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки настроек"
        error={error}
      />
    )
  }

  // === Разделение по категориям ===
  const defaultConfig = configs.find(cfg => cfg.isDefault)

  const currentConfig = configs.find(cfg => {
    const fromDate = cfg.activeFrom ? new Date(cfg.activeFrom) : null
    const untilDate = cfg.activeUntil ? new Date(cfg.activeUntil) : null

    if (!cfg.is_active) {
      return false
    }
    if (fromDate && untilDate) {
      return cfg.is_active && fromDate <= today && untilDate >= today
    }

    return false
  })
  
  const scheduled = configs.filter(cfg => {
    const fromDate = cfg.activeFrom ? new Date(cfg.activeFrom) : null
    return cfg.is_active && fromDate && fromDate > today
  })

  const others = configs.filter(cfg =>
    currentConfig?.id !== cfg.id && !scheduled.includes(cfg) && defaultConfig?.id !== cfg.id
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: 8 }}>
        <div>
          <FlexboxGrid justify="space-between" align="middle">
            <FlexboxGrid.Item>
              <h4 className="u-heading">Настройки главной страницы</h4>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item colspan={24}>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                <ButtonGroup>
                  {MAIN_PAGE_CONFIG_TYPE_OPTIONS.map(({ value, label }) => (
                    <Button
                      key={value}
                      appearance={selectedType === value ? 'primary' : 'ghost'}
                      onClick={() => setSelectedType(value)}
                    >
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
                <Button
                  appearance="primary"
                  as={Link}
                  to={{
                    pathname: generatePath(ROUTE_MAIN_PAGE_CONFIGS_ADD, {
                      brandSlug
                    }),
                    search: `?type=${selectedType}`
                  }}
                >
                  Создать новую
                </Button>
              </div>
            </FlexboxGrid.Item>
          </FlexboxGrid>
          <Divider style={{ margin: '8px 0' }} />
        </div>
        {currentConfig && (
          <>
            <h5>Текущая настройка</h5>
            <List bordered style={{ background: '#fff' }}>
              <ConfigListItem 
                config={currentConfig} 
                brandSlug={brandSlug} 
                isCurrent={true}
              />
            </List>
            <Divider />
          </>
        )}

        { defaultConfig && (
          <>
            <h5>Настройка по умолчанию</h5>
            <List bordered style={{ background: '#fff' }}>
              <ConfigListItem config={defaultConfig} brandSlug={brandSlug} />
            </List>
            <Divider />
          </>
        )}
  
        {scheduled.length > 0 && (
          <>
            <h5>Запланированные настройки</h5>
            <List bordered style={{ background: '#fff' }}>
              {
                scheduled.map(cfg => (
                  <ConfigListItem config={cfg} brandSlug={brandSlug} />
                ))
              }
            </List>
            <Divider />
          </>
        )}
  
        {others.length > 0 && (
          <>
            <h5>Прочие настройки</h5>
            <List bordered style={{ background: '#fff' }}>
              {
                others.map(cfg => (
                  <ConfigListItem config={cfg} brandSlug={brandSlug} />
                ))
              }
            </List>
          </>
        )}
      </div>

      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={data?.allMainPageConfigs?.pageInfo?.lastPage}
        maxButtons={10}
        onSelect={handlePageSelect}
        total={0}
        style={{ marginTop: 16 }}
      />
    </div>
  )
}
